import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button, Image, Col, Row } from "react-bootstrap";
import EditModal from "./EditModal";
import { FaEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { User, Users } from "../../types/Types";
import { useDeleteUser } from "../../api/mutations/useDeleteUser";
import { useAppContext } from "../../Context/AppContext";
import { useGetUser } from "../../api/queries/useGetUser";
import { useGetBilling } from "../../api/queries/useGetBilling";
import { useDeletePaymentMethod } from "../../api/mutations/useDeletePaymentMethod";
import { IoIosArrowDown } from "react-icons/io";
import { BsUpload } from "react-icons/bs";
import { IoLocationOutline } from "react-icons/io5";
import BusinessProfile from "./BusinessProfile";
import YouthProfile from "./YouthProfile";
import { FaUniversity } from "react-icons/fa";
import UploadImage from "./UploadImage";
import CurvedTextOverlay from "./CurvedTextOverlay";
import { useMediaQuery } from "react-responsive";
import AddPaymentMethodModal from "./AddPaymentMethodModal";

const MyAccount: React.FC = () => {
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [removingMethodId, setRemovingMethodId] = useState<string | null>(null);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const { objectId } = useAppContext();
  const { data: data } = useGetUser({ id: objectId || "" });
  const { data: paymentMethods, refetch: refetchPaymentMethods } =
    useGetBilling({ userId: objectId || "" });
  const userData = { ...data?.user, ...data?.imageUrls };
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = isDesktop ? 150 : isLgTablet ? 140 : isMdTablet ? 130 : 95;

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser({
    onSuccess: () => {
      setShowDeactivateModal(false);
      window.location.reload();
    },
  });

  const { mutate: deletePaymentMethod } = useDeletePaymentMethod({
    onSuccess: () => {
      setRemovingMethodId(null);
      setShowRemoveModal(false);
      refetchPaymentMethods();
    },
  });

  const handleEdit = () => {
    if (userData) setShowEditModal(true);
  };

  const handleDeactivate = () => {
    if (userData.id !== undefined) {
      deleteUser(userData.id);
    }
  };

  const handleConfirmRemove = () => {
    if (!selectedPaymentMethod?.id) return;
    setRemovingMethodId(selectedPaymentMethod.id);
    deletePaymentMethod(selectedPaymentMethod.id);
  };

  const fields = Object.entries(userData || {}).filter(
    ([key, value]) =>
      key.trim() !== "" &&
      value !== null &&
      value !== "" &&
      ![
        "password",
        "objectId",
        "isGDPRConsent",
        "isEmailUpdates",
        "isTermConditions",
        "isDirectory",
        "hasPaid",
        "hasCompletedProfile",
        "id",
        "membershipId",
        "consentToShareProfile",
        "isAdmin",
        "membershipType",
        "discountEndDate",
        "discountCode",
        "membershipName",
        "isAnnualMembership",
        "BusinessLogo",
        "PersonalProfessionalPhoto",
        "businessAddressLineTwo",
        "county",
        "businessAbout",
        "personalAbout",
        "businessProductServices",
        "businessDiscountCode",
        "businessDiscount",
        "ProfessionalPhoto",
        "skillsExperience",
      ].includes(key)
  );

  const totalPossibleFields = 18;

  const filledFieldsCount = fields.length;
  const completionPercent = Math.min(
    Math.round((filledFieldsCount / totalPossibleFields) * 100),
    100
  );

  return (
    <div className="p-4 pt-5 pt-md-5 m-0">
      <div className="card shadow-lg border-0 pb-2">
        <div>
          <div
            className={`position-relative ${
              userData.BannerImage ? "" : "gradient-bg"
            } profile-banner p-2 rounded-top`}
            style={{
              backgroundImage: `url(${userData.BannerImage || ""})`,
              backgroundSize: "cover", // Show full image without zoom/cropping
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              aspectRatio: "3 / 1",
            }}
          >
            <div className=" gradient-bg text-white d-flex justify-content-end align-items-center">
              <div className="dropdown position-absolute top-0 end-0 m-2">
                <button
                  className=" border-0 rounded-1 px-2 bg-transparent text-white"
                  id="accountActionsDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <IoIosArrowDown />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="accountActionsDropdown"
                >
                  <li>
                    <button className="dropdown-item" onClick={handleEdit}>
                      <FaEdit size={16} className="me-2" /> Edit
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => setShowDeactivateModal(true)}
                    >
                      <BsTrash size={16} className="me-2" /> Deactivate
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => setShowBannerModal(true)}
                    >
                      <BsUpload size={16} className="me-2" />{" "}
                      {userData.BannerImage ? "Change Banner" : "Add Banner"}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            {!userData.BannerImage && (
              <div className="d-flex h-100 justify-content-center align-items-center">
                <img
                  src="/SMBF-logo-white.png"
                  alt="logo"
                  className="w-25 h-25"
                />
              </div>
            )}
            <div className="position-absolute translate-middle image-container">
              <div className="position-relative w-100 h-100">
                <Image
                  src={
                    userData.ProfessionalPhoto ||
                    userData.PersonalProfessionalPhoto ||
                    "/user.png"
                  }
                  roundedCircle
                  alt="Profile"
                  className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                />

                <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                  <CurvedTextOverlay
                    size={imageSize}
                    stroke={
                      userData?.membershipName === "Youth Membership"
                        ? "#18c2c6"
                        : userData?.membershipName === "Business Membership"
                        ? "#1c57b9"
                        : "#544bc1"
                    }
                    text={
                      userData?.membershipName === "Youth Membership"
                        ? "#Youth"
                        : userData?.membershipName === "Business Membership"
                        ? "#Business"
                        : "#Executive"
                    }
                  />
                </span>
              </div>
            </div>
          </div>
          <div className="mx-3 pt-5 flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <div className="mt-md-4 mt-lg-5">
                <h2 className="mb-0">
                  {(userData.firstName || "").charAt(0).toUpperCase() +
                    (userData.firstName || "").slice(1) +
                    " " +
                    ((userData.lastName || "").charAt(0).toUpperCase() +
                      (userData.lastName || "").slice(1))}
                </h2>
                <span>
                  {userData.membershipName === "Youth Membership"
                    ? userData.studyInterest &&
                      userData.studyInterest.charAt(0).toUpperCase() +
                        userData.studyInterest.slice(1)
                    : userData.personalPositionCompany &&
                      userData.personalPositionCompany.charAt(0).toUpperCase() +
                        userData.personalPositionCompany.slice(1)}
                </span>
                {(userData.city || userData.country) && (
                  <div className="d-flex gap-1">
                    <IoLocationOutline className="mt-1" />
                    <span className="text-muted">
                      {userData.city}, {userData.country}
                    </span>
                  </div>
                )}
              </div>
              <div className="d-none d-md-block">
                {userData.membershipName === "Youth Membership" ? (
                  <div className="fw-semibold d-flex gap-1 align-items-center">
                    <FaUniversity size={25} />
                    {userData.universityCollege}
                  </div>
                ) : (
                  <div className="d-flex gap-3 align-items-center cursor-pointer">
                    <Image
                      src={userData.BusinessLogo}
                      className="rounded-circle"
                      width={40}
                      height={40}
                      style={{ objectFit: "contain" }}
                      alt={"logo"}
                    />
                    <div className="fw-semibold">{userData.businessName}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {completionPercent < 100 && (
        <div className="progress mt-3" style={{ height: "15px" }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${completionPercent}%` }}
            aria-valuenow={completionPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span className={"text-black-md-white"}>
              {completionPercent}% Profile Completed
            </span>
          </div>
        </div>
      )}
      {userData.membershipName === "Youth Membership"
        ? userData.id && <YouthProfile userData={userData as User} />
        : userData.id && <BusinessProfile userData={userData as User} />}

      {/* Manage Membership */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Manage Membership</h5>
        </div>
        <div className="p-3">
          <p className="text-muted small mb-3">
            Upgrade or cancel your membership.
          </p>
          <Button
            variant="outline-primary"
            className="w-100"
            onClick={() => navigate("/manage-membership")}
          >
            Manage Membership
          </Button>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="card shadow-lg border-0 mt-3">
        <div className="d-flex px-3 px-md-4 py-3 gradient-bg text-white align-items-center rounded-top">
          <h5 className="mb-0">Payment Methods</h5>
        </div>
        <div className="p-3">
          {paymentMethods && paymentMethods.length > 0 ? (
            <>
              {paymentMethods
                .filter((method: any) => method?.last4)
                .filter(
                  (method: any, index: number, self: any[]) =>
                    index ===
                    self.findIndex(
                      (m) => m.brand === method.brand && m.last4 === method.last4
                    )
                )
                .map((method: any, index: number) => (
                  <div
                    key={method.id ?? index}
                    className="d-flex justify-content-between align-items-center border p-2 rounded mb-2"
                  >
                    <div>
                      <p className="mb-1 fw-bold">
                        {method.brand?.toUpperCase()} •••• {method.last4}
                      </p>
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setSelectedPaymentMethod(method);
                        setShowRemoveModal(true);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={() => setShowAddCardModal(true)}
              >
                Add payment method
              </Button>
            </>
          ) : (
            <>
              <p className="text-muted small mb-2">No saved payment methods.</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddCardModal(true)}
              >
                Add payment method
              </Button>
            </>
          )}
        </div>
      </div>

      <AddPaymentMethodModal
        show={showAddCardModal}
        onHide={() => setShowAddCardModal(false)}
        userId={objectId || ""}
        onSuccess={() => refetchPaymentMethods()}
      />

      {/* Modals */}
      <EditModal
        showEditModal={showEditModal}
        setShowEditModal={() => setShowEditModal(false)}
        userData={userData && "id" in userData ? (data as Users) : null}
        completionPercent={completionPercent}
      />
      <Modal
        centered
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate your account? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeactivateModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeactivate}
            disabled={isDeleting}
          >
            {isDeleting ? "Deactivating..." : "Deactivate"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Payment Method</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove this payment method (
          <strong>
            {selectedPaymentMethod?.brand?.toUpperCase()} ••••
            {selectedPaymentMethod?.last4}
          </strong>
          )?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={removingMethodId === selectedPaymentMethod?.id}
          >
            {removingMethodId === selectedPaymentMethod?.id
              ? "Removing..."
              : "Remove"}
          </Button>
        </Modal.Footer>
      </Modal>
      <UploadImage
        showBannerModal={showBannerModal}
        setShowBannerModal={setShowBannerModal}
        userData={userData as User}
      />
    </div>
  );
};

export default MyAccount;